// src/publish/pieces/blogSeo.js — pieces of the SEO blog article (format key 'blog-seo').
//
// The SEO Agent answers with structured fields (title, hook, sections[] with
// h2 / content / subsections[] h3 / content, conclusion, faq[], meta_title,
// meta_description, slug, internal_links[], body_html, references[]); the
// sources the references point to sit on results.sourcesConsulted (the
// backend's sources_consulted, BE >= e162398). Pieces, in this order (ids pinned
// by the sample's tobe/pezzi.json), each only when its field is non-empty:
//   article          primary, 'rich' — h1 title, hook paragraphs, h2 sections
//                    with their content and h3 subsections, then an h2
//                    «Conclusione» (i18n) + the conclusion paragraphs
//   h1               the title alone
//   meta_title       limit 60       meta_description   limit 160
//   slug             kind 'url' — "/<slug>" as literal text: it is a path, not
//                    an absolute URL, so no link run is built around it
//   faq              kind 'list' — one qa block per question
//   internal_links   kind 'list' — "anchor → url" per link, literal text
//   sources          kind 'list' — one "[N] title — url" per content.references
//                    entry ({marker, source_id}), the source resolved in
//                    sourcesConsulted by claimsModel.resolveReferences: the url
//                    only when http(s), «(non apribile dai lettori)» after the
//                    title of a source readers cannot open, «fonte non
//                    indicata» for a source the run does not name (i18n
//                    pieces.ui.*). LITERAL text: the number is the reference
//                    the reader looks up, so it survives every serialisation
//                    (it is not a cite run). ⛔ Never parsed from body_html.
//   html             kind 'source' — body_html VERBATIM in one paragraph of
//                    literal runs (the Markdown fence / <pre> reads it back)
// The long fields may carry inline HTML and "[N]" markers: every one goes
// through normalize.js once. Titles and URLs of the referenced sources are
// backend data, never re-parsed as markup. Nothing here reads full_text.

import i18n from '../../i18n/index.js';
import { resolveReferences } from '../../components/claims/claimsModel.js';
import { heading, paragraph, qa, list, textRuns } from '../blocks.js';
import { normalizeText, inlineRuns } from '../normalize.js';
import { makePiece, pieceLabel, ensureOnePrimary, str, arr, obj, LIMITS } from '../piece.js';

const FORMAT = 'blog-seo';

/** The sections of a structured article → heading + content blocks (shared with the newsletter). */
export function sectionBlocks(sections) {
  const out = [];
  for (const raw of arr(sections)) {
    const s = obj(raw);
    const h2 = inlineRuns(str(s.h2).trim());
    if (h2.length) out.push(heading(2, h2));
    out.push(...normalizeText(str(s.content)));
    for (const subRaw of arr(s.subsections)) {
      const sub = obj(subRaw);
      const h3 = inlineRuns(str(sub.h3).trim());
      if (h3.length) out.push(heading(3, h3));
      out.push(...normalizeText(str(sub.content)));
    }
  }
  return out;
}

/**
 * content.references + results.sourcesConsulted → the one list block of the
 * 'sources' piece, in the backend's order, [] when the article carries no
 * reference. A reference whose source the run does not name is still listed
 * (its "[N]" is in the text): «[N] fonte non indicata».
 */
export function referencesListBlocks(references, sourcesConsulted) {
  const refs = resolveReferences(references, sourcesConsulted);
  if (!refs.length) return [];
  const unknown = i18n.t('outputs:pieces.ui.referenceUnknown');
  const unlinked = i18n.t('outputs:pieces.ui.referenceUnlinked');
  const items = refs.map((ref) => {
    const title = str(ref.title).trim() || unknown;
    if (ref.url) return textRuns(`[${ref.marker}] ${title} — ${ref.url}`);
    return textRuns(ref.title ? `[${ref.marker}] ${title} ${unlinked}` : `[${ref.marker}] ${unknown}`);
  });
  return [list(items, false)];
}

function faqBlocks(faq) {
  return arr(faq).map(obj)
    .map((f) => ({ q: inlineRuns(str(f.question).trim()), a: normalizeText(str(f.answer)) }))
    .filter(({ q, a }) => q.length || a.length)
    .map(({ q, a }) => qa(q, a));
}

/**
 * content.internal_links → [{ anchor, url }] with the backend's coercions applied
 * (a wrong type is an empty value), entries with neither dropped. The one reader
 * of that field: the 'internal_links' piece below and the blog screen's HTML tab
 * (components/outputs/blogSeo/BlogSeoCard.jsx) both start from it.
 */
export function internalLinkEntries(content) {
  return arr(obj(content).internal_links).map(obj)
    .map((l) => ({ anchor: str(l.anchor_text).trim(), url: str(l.url).trim() }))
    .filter(({ anchor, url }) => anchor || url);
}

function internalLinkItems(content) {
  return internalLinkEntries(content)
    .map(({ anchor, url }) => textRuns([anchor, url].filter(Boolean).join(' → ')));
}

export function build(entry) {
  const content = entry?.results?.content;
  if (!content || typeof content !== 'object') return [];
  const pieces = [];

  const title = str(content.title).trim();
  const article = [];
  if (title) article.push(heading(1, inlineRuns(title)));
  article.push(...normalizeText(str(content.hook)));
  article.push(...sectionBlocks(content.sections));
  const conclusion = normalizeText(str(content.conclusion));
  if (conclusion.length) {
    article.push(heading(2, pieceLabel(FORMAT, 'conclusion')));
    article.push(...conclusion);
  }
  if (article.length) pieces.push(makePiece(FORMAT, { id: 'article', kind: 'rich', blocks: article }));

  if (title) pieces.push(makePiece(FORMAT, { id: 'h1', blocks: [paragraph(inlineRuns(title))] }));

  const metaTitle = str(content.meta_title).trim();
  if (metaTitle) pieces.push(makePiece(FORMAT, { id: 'meta_title', blocks: [paragraph(inlineRuns(metaTitle))], limit: LIMITS.blogMetaTitle }));

  const metaDescription = str(content.meta_description).trim();
  if (metaDescription) {
    pieces.push(makePiece(FORMAT, { id: 'meta_description', blocks: [paragraph(inlineRuns(metaDescription))], limit: LIMITS.blogMetaDescription }));
  }

  const slug = str(content.slug).trim().replace(/^\/+/, '');
  if (slug) pieces.push(makePiece(FORMAT, { id: 'slug', kind: 'url', blocks: [paragraph(textRuns(`/${slug}`))] }));

  const faq = faqBlocks(content.faq);
  if (faq.length) pieces.push(makePiece(FORMAT, { id: 'faq', kind: 'list', blocks: faq }));

  const links = internalLinkItems(content);
  if (links.length) pieces.push(makePiece(FORMAT, { id: 'internal_links', kind: 'list', blocks: [list(links, false)] }));

  const sources = referencesListBlocks(content.references, entry?.results?.sourcesConsulted);
  if (sources.length) pieces.push(makePiece(FORMAT, { id: 'sources', kind: 'list', blocks: sources }));

  const html = str(content.body_html);
  if (html.trim()) pieces.push(makePiece(FORMAT, { id: 'html', kind: 'source', blocks: [paragraph(textRuns(html))] }));

  return ensureOnePrimary(pieces, ['article']);
}
